import React, { useState, useEffect } from 'react';
import { useStores, put, remove, generateId } from '../hooks/useDB';
import PageLoader from '../components/PageLoader';
import { Plus, Trash2, Edit2, X, Paperclip, Receipt, Search, Users, Inbox, Loader2 } from 'lucide-react';
import { toast } from '../utils/toast';
import { confirm } from '../utils/confirm';
import { isValidEmail } from '../utils/formatters';

const ROLES = ['Engineering', 'Design', 'Product', 'Marketing', 'Sales', 'Operations', 'HR', 'Finance', 'Leadership', 'Other'];
const RSVP = ['pending', 'confirmed', 'declined', 'maybe'];
const DIET = ['None', 'Vegetarian', 'Vegan', 'Jain', 'Gluten-free', 'Halal', 'Other'];
const BLOOD_GROUPS = ['Unknown', 'A+', 'A-', 'B+', 'B-', 'AB+', 'AB-', 'O+', 'O-'];
const TRAVEL_STATUS = ['pending', 'booked', 'issued', 'completed', 'cancelled'];

function Modal({ person, onSave, onClose }) {
  const [form, setForm] = useState(person || {
    name: '', email: '', phone: '', role: 'Engineering',
    rsvp: 'pending', dietary: 'None', tshirtSize: 'M',
    bloodGroup: 'Unknown', originCity: '',
    roomNeeded: false, notes: '',
    travelMode: 'cab', travelStatus: 'pending',
    fromCity: '', toCity: '', departureDate: '', returnDate: '',
    cabBookingRef: '', cabVendor: '',
    flightPnr: '', flightNo: '',
    travelCost: ''
  });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  useEffect(() => {
    const handler = (e) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [onClose]);

  const handleSave = async () => {
    if (!form.name.trim()) { setError('Attendee name is required.'); return; }
    if (form.email && !isValidEmail(form.email)) { setError('Please enter a valid email address.'); return; }
    if (form.travelCost && Number(form.travelCost) < 0) { setError('Travel cost cannot be negative.'); return; }
    if (form.departureDate && form.returnDate && form.returnDate < form.departureDate) { setError('Return date must be on or after departure date.'); return; }
    setSaving(true);
    setError('');
    try {
      await onSave(form);
    } catch {
      setError('Failed to save. Please try again.');
      setSaving(false);
    }
  };

  return (
    <div className="tw-drawer-backdrop">
      <div className="tw-drawer-panel">
        {/* Header */}
        <div className="p-6 border-b border-slate-100 flex items-center justify-between sticky top-0 bg-white z-10">
          <div>
            <h3 className="text-lg font-semibold text-slate-900">{person ? 'Edit Attendee' : 'Register New Attendee'}</h3>
            <p className="text-xs text-slate-500 mt-1">Configure profile, RSVP choices and airline or transit details</p>
          </div>
          <button className="p-2 text-slate-400 hover:text-slate-600 hover:bg-slate-50 rounded-lg transition" onClick={onClose}>
            <X size={16} />
          </button>
        </div>

        {/* Body */}
        <div className="p-6 space-y-5 flex-1">
          <div className="space-y-1">
            <label className="text-xs font-semibold text-slate-600 uppercase tracking-wider">Full Name *</label>
            <input 
              value={form.name} 
              onChange={e => set('name', e.target.value)} 
              placeholder="Full name of attendee"
              className="w-full bg-slate-50 border border-slate-200 rounded-lg px-3 py-2 text-sm focus:bg-white focus:border-slate-800 focus:ring-1 focus:ring-slate-800 outline-none transition" 
            />
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div className="space-y-1">
              <label className="text-xs font-semibold text-slate-600 uppercase tracking-wider">Email Address</label>
              <input 
                type="email" 
                value={form.email} 
                onChange={e => set('email', e.target.value)} 
                placeholder="name@nativeorange.ai"
                className="w-full bg-slate-50 border border-slate-200 rounded-lg px-3 py-2 text-sm focus:bg-white focus:border-slate-800 outline-none transition" 
              />
            </div>
            <div className="space-y-1">
              <label className="text-xs font-semibold text-slate-600 uppercase tracking-wider">Phone</label>
              <input 
                value={form.phone} 
                onChange={e => set('phone', e.target.value)} 
                placeholder="+91-XXXXX-XXXXX"
                className="w-full bg-slate-50 border border-slate-200 rounded-lg px-3 py-2 text-sm focus:bg-white focus:border-slate-800 outline-none transition" 
              />
            </div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div className="space-y-1">
              <label className="text-xs font-semibold text-slate-600 uppercase tracking-wider">Department / Team</label>
              <select 
                value={form.role} 
                onChange={e => set('role', e.target.value)}
                className="w-full bg-slate-50 border border-slate-200 rounded-lg px-3 py-2 text-sm focus:bg-white focus:border-slate-800 outline-none transition animate-none"
              >
                {ROLES.map(r => <option key={r} value={r}>{r}</option>)}
              </select>
            </div>
            <div className="space-y-1">
              <label className="text-xs font-semibold text-slate-600 uppercase tracking-wider">RSVP status</label>
              <select 
                value={form.rsvp} 
                onChange={e => set('rsvp', e.target.value)}
                className="w-full bg-slate-50 border border-slate-200 rounded-lg px-3 py-2 text-sm focus:bg-white focus:border-slate-800 outline-none transition"
              >
                {RSVP.map(r => <option key={r} value={r}>{r}</option>)}
              </select>
            </div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div className="space-y-1">
              <label className="text-xs font-semibold text-slate-600 uppercase tracking-wider">Dietary Preference</label>
              <select 
                value={form.dietary} 
                onChange={e => set('dietary', e.target.value)}
                className="w-full bg-slate-50 border border-slate-200 rounded-lg px-3 py-2 text-sm focus:bg-white focus:border-slate-800 outline-none transition"
              >
                {DIET.map(d => <option key={d} value={d}>{d}</option>)}
              </select>
            </div>
            <div className="space-y-1">
              <label className="text-xs font-semibold text-slate-600 uppercase tracking-wider">T-Shirt Size</label>
              <select 
                value={form.tshirtSize} 
                onChange={e => set('tshirtSize', e.target.value)}
                className="w-full bg-slate-50 border border-slate-200 rounded-lg px-3 py-2 text-sm focus:bg-white focus:border-slate-800 outline-none transition"
              >
                {['XS','S','M','L','XL','XXL'].map(s => <option key={s} value={s}>{s}</option>)}
              </select>
            </div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div className="space-y-1">
              <label className="text-xs font-semibold text-slate-600 uppercase tracking-wider">Blood Group</label>
              <select 
                value={form.bloodGroup || 'Unknown'} 
                onChange={e => set('bloodGroup', e.target.value)}
                className="w-full bg-slate-50 border border-slate-200 rounded-lg px-3 py-2 text-sm focus:bg-white focus:border-slate-800 outline-none transition"
              >
                {BLOOD_GROUPS.map(b => <option key={b} value={b}>{b}</option>)}
              </select>
            </div>
            <div className="space-y-1">
              <label className="text-xs font-semibold text-slate-600 uppercase tracking-wider">Origin City / Hometown</label>
              <input 
                value={form.originCity || ''} 
                onChange={e => set('originCity', e.target.value)} 
                placeholder="e.g. Bangalore, Chennai"
                className="w-full bg-slate-50 border border-slate-200 rounded-lg px-3 py-2 text-sm focus:bg-white focus:border-slate-800 outline-none transition" 
              />
            </div>
          </div>

          <div className="flex items-center gap-3 bg-slate-50 border border-slate-100 p-3 rounded-lg">
            <input 
              type="checkbox" 
              id="room" 
              checked={form.roomNeeded} 
              onChange={e => set('roomNeeded', e.target.checked)} 
              className="w-4 h-4 rounded text-slate-800 border-slate-300 focus:ring-slate-800 cursor-pointer" 
            />
            <label htmlFor="room" className="text-sm font-medium text-slate-700 cursor-pointer select-none">
              Stay Accommodation Required
            </label>
          </div>

          {/* Travel Details card */}
          <div className="border border-slate-200 rounded-xl p-4 bg-slate-50/50 space-y-4">
            <h4 className="text-xs font-bold text-slate-800 uppercase tracking-wider flex items-center gap-2">
              ✈️ Transit & Tickets Coordinator
            </h4>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1">
                <label className="text-[10px] font-bold text-slate-500 uppercase">Travel Mode</label>
                <select 
                  value={form.travelMode || 'cab'} 
                  onChange={e => set('travelMode', e.target.value)}
                  className="w-full bg-white border border-slate-200 rounded-md px-2 py-1 text-xs focus:border-slate-800 outline-none"
                >
                  <option value="cab">Cab / Sedan</option>
                  <option value="flight">Airline / Flight</option>
                  <option value="both">Both Flight & Cab</option>
                  <option value="self">Self Transit</option>
                </select>
              </div>
              <div className="space-y-1">
                <label className="text-[10px] font-bold text-slate-500 uppercase">Travel Status</label>
                <select 
                  value={form.travelStatus || 'pending'} 
                  onChange={e => set('travelStatus', e.target.value)}
                  className="w-full bg-white border border-slate-200 rounded-md px-2 py-1 text-xs focus:border-slate-800 outline-none"
                >
                  {TRAVEL_STATUS.map(s => <option key={s} value={s}>{s}</option>)}
                </select>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1">
                <label className="text-[10px] font-bold text-slate-500 uppercase">From City</label>
                <input 
                  value={form.fromCity || ''} 
                  onChange={e => set('fromCity', e.target.value)} 
                  placeholder="e.g. Bangalore"
                  className="w-full bg-white border border-slate-200 rounded-md px-2 py-1 text-xs focus:border-slate-800 outline-none"
                />
              </div>
              <div className="space-y-1">
                <label className="text-[10px] font-bold text-slate-500 uppercase">To City</label>
                <input 
                  value={form.toCity || ''} 
                  onChange={e => set('toCity', e.target.value)} 
                  placeholder="e.g. Goa"
                  className="w-full bg-white border border-slate-200 rounded-md px-2 py-1 text-xs focus:border-slate-800 outline-none"
                />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1">
                <label className="text-[10px] font-bold text-slate-500 uppercase">Departure Date</label>
                <input 
                  type="date" 
                  value={form.departureDate || ''} 
                  onChange={e => set('departureDate', e.target.value)} 
                  className="w-full bg-white border border-slate-200 rounded-md px-2 py-1 text-xs focus:border-slate-800 outline-none"
                />
              </div>
              <div className="space-y-1">
                <label className="text-[10px] font-bold text-slate-500 uppercase">Return Date</label>
                <input 
                  type="date" 
                  value={form.returnDate || ''} 
                  onChange={e => set('returnDate', e.target.value)} 
                  className="w-full bg-white border border-slate-200 rounded-md px-2 py-1 text-xs focus:border-slate-800 outline-none"
                />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1">
                <label className="text-[10px] font-bold text-slate-500 uppercase">Cab Booking Ref</label>
                <input 
                  value={form.cabBookingRef || ''} 
                  onChange={e => set('cabBookingRef', e.target.value)} 
                  placeholder="OLA-998"
                  className="w-full bg-white border border-slate-200 rounded-md px-2 py-1 text-xs focus:border-slate-800 outline-none"
                />
              </div>
              <div className="space-y-1">
                <label className="text-[10px] font-bold text-slate-500 uppercase">Flight PNR Code</label>
                <input 
                  value={form.flightPnr || ''} 
                  onChange={e => set('flightPnr', e.target.value)} 
                  placeholder="PNR-88A92"
                  className="w-full bg-white border border-slate-200 rounded-md px-2 py-1 text-xs focus:border-slate-800 outline-none"
                />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1">
                <label className="text-[10px] font-bold text-slate-500 uppercase">Airline Code / No</label>
                <input 
                  value={form.flightNo || ''} 
                  onChange={e => set('flightNo', e.target.value)} 
                  placeholder="AI-102"
                  className="w-full bg-white border border-slate-200 rounded-md px-2 py-1 text-xs focus:border-slate-800 outline-none"
                />
              </div>
              <div className="space-y-1">
                <label className="text-[10px] font-bold text-slate-500 uppercase">Travel Cost (INR)</label>
                <input 
                  type="number" 
                  value={form.travelCost || ''} 
                  onChange={e => set('travelCost', e.target.value)} 
                  placeholder="7500"
                  className="w-full bg-white border border-slate-200 rounded-md px-2 py-1 text-xs focus:border-slate-800 outline-none"
                />
              </div>
            </div>
          </div>

          <div className="space-y-1">
            <label className="text-xs font-semibold text-slate-600 uppercase tracking-wider">Notes & Comments</label>
            <textarea 
              value={form.notes} 
              onChange={e => set('notes', e.target.value)} 
              rows={2} 
              placeholder="Special instructions, dietary notes, etc."
              className="w-full bg-slate-50 border border-slate-200 rounded-lg px-3 py-2 text-sm focus:bg-white focus:border-slate-800 outline-none transition resize-none"
            />
          </div>
        </div>

        {/* Footer */}
        <div className="p-6 border-t border-slate-100 bg-white">
          {error && <div className="mb-3 text-xs text-red-600 bg-red-50 border border-red-200 rounded-lg px-3 py-2">{error}</div>}
          <div className="flex gap-3 justify-end">
            <button className="px-4 py-2 text-sm font-semibold border border-slate-200 hover:bg-slate-50 rounded-lg transition" onClick={onClose} disabled={saving}>
              Cancel
            </button>
            <button className="px-4 py-2 text-sm font-semibold bg-slate-900 hover:bg-slate-800 text-white rounded-lg transition flex items-center gap-2 disabled:opacity-60" onClick={handleSave} disabled={saving}>
              {saving && <Loader2 size={14} className="animate-spin" />}
              {person ? 'Save Changes' : 'Register Attendee'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

function readFiles(files) {
  const valid = Array.from(files || []).filter((file) => file.type.startsWith('image/') || file.type === 'application/pdf');
  return Promise.all(valid.map((file) => new Promise((resolve) => {
    const reader = new FileReader();
    reader.onload = () => resolve({
      id: generateId(),
      name: file.name,
      type: file.type,
      size: file.size,
      dataUrl: reader.result,
      uploadedAt: new Date().toISOString()
    });
    reader.readAsDataURL(file);
  })));
}

function InvoiceModal({ attendee, onSave, onClose }) {
  const [form, setForm] = useState({
    title: '',
    category: 'Transport',
    amount: '',
    vendor: '',
    date: new Date().toISOString().slice(0, 10),
    status: 'pending',
    notes: '',
    attachments: []
  });
  const set = (k, v) => setForm((prev) => ({ ...prev, [k]: v }));

  const addAttachment = async (files) => {
    const encoded = await readFiles(files);
    setForm((prev) => ({ ...prev, attachments: [...prev.attachments, ...encoded] }));
  };

  const removeAttachment = (id) => {
    setForm((prev) => ({ ...prev, attachments: prev.attachments.filter((file) => file.id !== id) }));
  };

  const save = () => {
    if (!form.title || !form.amount || !form.attachments.length) return;
    onSave({
      ...form,
      type: 'expense',
      estimated: form.amount,
      paymentMethod: 'reimbursement',
      reimbursement: true,
      submittedByEmail: attendee.email,
      submittedByName: attendee.name,
      assignedAttendeeIds: [attendee.id],
      submittedAt: new Date().toISOString(),
      status: 'pending'
    });
  };

  return (
    <div className="tw-drawer-backdrop">
      <div className="tw-drawer-panel">
        <div className="p-6 border-b border-slate-100 flex items-center justify-between sticky top-0 bg-white z-10">
          <div>
            <h3 className="text-lg font-semibold text-slate-900">Upload Invoice</h3>
            <p className="text-xs text-slate-500 mt-1">Submit expenditures and receipts under {attendee.name}</p>
          </div>
          <button className="p-2 text-slate-400 hover:text-slate-600 hover:bg-slate-50 rounded-lg transition" onClick={onClose}>
            <X size={16} />
          </button>
        </div>

        <div className="p-6 space-y-4">
          <div className="border border-slate-100 rounded-lg p-3 bg-slate-50">
            <div className="font-semibold text-slate-800 text-sm">{attendee.name}</div>
            <div className="text-xs text-slate-500">{attendee.email || 'No email registered'}</div>
          </div>

          <div className="space-y-1">
            <label className="text-xs font-semibold text-slate-600 uppercase tracking-wider">Expense Title *</label>
            <input 
              value={form.title} 
              onChange={(e) => set('title', e.target.value)} 
              placeholder="e.g., Delhi taxi cab reimbursement" 
              className="w-full bg-slate-50 border border-slate-200 rounded-lg px-3 py-2 text-sm focus:bg-white focus:border-slate-800 outline-none transition"
            />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-1">
              <label className="text-xs font-semibold text-slate-600 uppercase tracking-wider">Category</label>
              <select 
                value={form.category} 
                onChange={(e) => set('category', e.target.value)}
                className="w-full bg-slate-50 border border-slate-200 rounded-lg px-3 py-2 text-sm focus:bg-white focus:border-slate-800 outline-none transition"
              >
                {['Transport', 'Accommodation', 'Meals', 'Event Supplies', 'Misc'].map((cat) => <option key={cat} value={cat}>{cat}</option>)}
              </select>
            </div>
            <div className="space-y-1">
              <label className="text-xs font-semibold text-slate-600 uppercase tracking-wider">Amount (INR) *</label>
              <input 
                type="number" 
                value={form.amount} 
                onChange={(e) => set('amount', e.target.value)} 
                placeholder="4500"
                className="w-full bg-slate-50 border border-slate-200 rounded-lg px-3 py-2 text-sm focus:bg-white focus:border-slate-800 outline-none transition"
              />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-1">
              <label className="text-xs font-semibold text-slate-600 uppercase tracking-wider">Vendor / Payee</label>
              <input 
                value={form.vendor} 
                onChange={(e) => set('vendor', e.target.value)} 
                placeholder="Ola Cabs"
                className="w-full bg-slate-50 border border-slate-200 rounded-lg px-3 py-2 text-sm focus:bg-white focus:border-slate-800 outline-none transition"
              />
            </div>
            <div className="space-y-1">
              <label className="text-xs font-semibold text-slate-600 uppercase tracking-wider">Date</label>
              <input 
                type="date" 
                value={form.date} 
                onChange={(e) => set('date', e.target.value)} 
                className="w-full bg-slate-50 border border-slate-200 rounded-lg px-3 py-2 text-sm focus:bg-white"
              />
            </div>
          </div>

          <div className="space-y-1">
            <label className="text-xs font-semibold text-slate-600 uppercase tracking-wider">Upload receipt files (PDF/Images) *</label>
            <input 
              type="file" 
              accept="application/pdf,image/*" 
              multiple 
              onChange={(e) => addAttachment(e.target.files)} 
              className="w-full text-xs"
            />
            {form.attachments.map((file) => (
              <div key={file.id} className="flex items-center justify-between p-2 border border-slate-100 bg-slate-50 rounded-lg text-xs mt-2">
                <span className="text-slate-600 flex items-center gap-1"><Paperclip size={12} /> {file.name}</span>
                <button className="px-2 py-1 text-[10px] bg-red-50 text-red-600 border border-red-200 rounded-md" onClick={() => removeAttachment(file.id)}>Remove</button>
              </div>
            ))}
          </div>

          <div className="space-y-1">
            <label className="text-xs font-semibold text-slate-600 uppercase tracking-wider">Notes</label>
            <textarea 
              value={form.notes} 
              onChange={(e) => set('notes', e.target.value)} 
              rows={2} 
              className="w-full bg-slate-50 border border-slate-200 rounded-lg px-3 py-2 text-sm focus:bg-white focus:border-slate-800 outline-none resize-none"
            />
          </div>
        </div>

        <div className="p-6 border-t border-slate-100 flex gap-3 justify-end bg-white">
          <button className="px-4 py-2 text-sm font-semibold border border-slate-200 hover:bg-slate-50 rounded-lg transition" onClick={onClose}>
            Cancel
          </button>
          <button className="px-4 py-2 text-sm font-semibold bg-slate-900 text-white hover:bg-slate-800 rounded-lg transition" onClick={save}>
            Save Invoice
          </button>
        </div>
      </div>
    </div>
  );
}

const rsvpClass = { confirmed: 'sh-badge-success', declined: 'sh-badge-danger', pending: 'sh-badge-warning', maybe: 'sh-badge-info' };
const travelClass = { pending: 'sh-badge-warning', booked: 'sh-badge-info', issued: 'sh-badge-success', completed: 'sh-badge-success', cancelled: 'sh-badge-danger' };

const ATTENDEE_STORES = ['attendees', 'budget'];

export default function Attendees() {
  const { attendees, budget: budgetEntries, loading } = useStores(ATTENDEE_STORES);
  const [modal, setModal] = useState(null);
  const [invoiceFor, setInvoiceFor] = useState(null);
  const [filter, setFilter] = useState('all');
  const [query, setQuery] = useState('');

  const save = async (form) => {
    await put('attendees', { ...form, id: form.id || generateId() });
    setModal(null);
    window.dispatchEvent(new CustomEvent('planner:data-change'));
    toast.success(form.id ? 'Attendee updated.' : 'Attendee registered.');
  };

  const saveInvoice = async (form) => {
    await put('budget', { ...form, id: generateId() });
    setInvoiceFor(null);
    window.dispatchEvent(new CustomEvent('planner:data-change'));
    toast.success('Invoice submitted.');
  };

  const del = async (id) => {
    const ok = await confirm('Remove this attendee from the registry? This cannot be undone.', 'Delete Attendee');
    if (ok) {
      try {
        await remove('attendees', id);
        window.dispatchEvent(new CustomEvent('planner:data-change'));
        toast.success('Attendee removed.');
      } catch {
        toast.error('Failed to delete attendee.');
      }
    }
  };

  const searched = attendees.filter((a) => {
    const needle = query.trim().toLowerCase();
    if (!needle) return true;
    return [a.name, a.email, a.phone, a.role].filter(Boolean).join(' ').toLowerCase().includes(needle);
  });
  const filtered = filter === 'all' ? searched : searched.filter(a => a.rsvp === filter);
  const travelBooked = attendees.filter((a) => ['booked', 'issued', 'completed'].includes(a.travelStatus)).length;
  const travelPending = attendees.filter((a) => (a.travelStatus || 'pending') === 'pending').length;
  const travelCostTotal = attendees.reduce((s, a) => s + (parseFloat(a.travelCost) || 0), 0);

  const expenseMap = budgetEntries
    .filter((e) => e.type === 'expense' && e.assignedAttendeeIds?.length)
    .reduce((map, row) => {
      const attendeeId = row.assignedAttendeeIds[0];
      map[attendeeId] = (map[attendeeId] || 0) + (parseFloat(row.amount) || 0);
      return map;
    }, {});

  if (loading) return <PageLoader label="Loading attendees…" sublabel="Fetching the offsite registry" />;

  return (
    <div className="space-y-6">
      {/* Page Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h2 className="text-2xl font-bold font-display text-slate-900 flex items-center gap-2">
            <Users style={{ color: 'var(--accent)' }} size={24} />
            <span>Offsite Attendees Registry</span>
          </h2>
          <p className="text-sm text-slate-500 mt-1">
            Maintain confirmed RSVPs, room codes, flight PNR vouchers, and claim invoices
          </p>
        </div>
        <button 
          className="px-4 py-2 bg-slate-900 text-white rounded-lg text-sm font-semibold hover:bg-slate-800 transition flex items-center gap-2 self-start sm:self-auto shadow-sm"
          onClick={() => setModal('new')}
        >
          <Plus size={16} />
          <span>Add Attendee</span>
        </button>
      </div>

      {/* Stats Summary Panel */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <div className="sh-stat-card">
          <div className="sh-stat-head">
            <span className="sh-stat-label">Travel Booked</span>
          </div>
          <div className="sh-stat-value text-slate-900">{travelBooked}</div>
          <p className="text-xs text-slate-500">Scheduled tickets issued & booked</p>
        </div>
        <div className="sh-stat-card">
          <div className="sh-stat-head">
            <span className="sh-stat-label">Travel Pending</span>
          </div>
          <div className="sh-stat-value text-amber-600">{travelPending}</div>
          <p className="text-xs text-slate-500">Need urgent corporate card booking</p>
        </div>
        <div className="sh-stat-card">
          <div className="sh-stat-head">
            <span className="sh-stat-label">Total Travel Cost</span>
          </div>
          <div className="sh-stat-value text-slate-900">₹{travelCostTotal.toLocaleString('en-IN')}</div>
          <p className="text-xs text-slate-500">Accumulated flight/cab payouts</p>
        </div>
      </div>

      {/* Filters & search deck */}
      <div className="bg-white border border-slate-200 rounded-xl p-5 shadow-sm space-y-4">
        <div className="space-y-1">
          <label className="text-xs font-semibold text-slate-500 uppercase">Interactive Search filter</label>
          <div className="relative">
            <Search size={16} className="absolute left-3 top-3.5 text-slate-400" />
            <input 
              value={query} 
              onChange={(e) => setQuery(e.target.value)} 
              placeholder="Search by name, email, phone, or role..." 
              className="w-full bg-slate-100 border border-slate-200 rounded-lg pl-9 pr-4 py-2 text-sm focus:bg-white focus:border-slate-800 outline-none transition" 
            />
          </div>
        </div>

        <div className="space-y-2">
          <span className="text-xs font-bold text-slate-500 uppercase block">RSVP Filters</span>
          <div className="flex flex-wrap gap-2">
            <button 
              className={`px-3 py-1.5 rounded-lg text-xs font-semibold border transition ${filter === 'all' ? 'bg-slate-900 text-white border-slate-900' : 'bg-white text-slate-600 border-slate-200 hover:bg-slate-50'}`}
              onClick={() => setFilter('all')}
            >
              All Registry ({attendees.length})
            </button>
            {RSVP.map(f => (
              <button 
                key={f} 
                className={`px-3 py-1.5 rounded-lg text-xs font-semibold border transition capitalize ${filter === f ? 'bg-slate-900 text-white border-slate-900' : 'bg-white text-slate-600 border-slate-200 hover:bg-slate-50'}`}
                onClick={() => setFilter(f)}
              >
                {f} ({attendees.filter(a => a.rsvp === f).length})
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Main Roster Registry Table */}
      {filtered.length === 0 ? (
        <div className="bg-white border border-slate-200 rounded-xl p-12 text-center">
          <Inbox className="mx-auto text-slate-300 mb-3" size={36} />
          <h3 className="text-base font-bold text-slate-800">No matching attendees</h3>
          <p className="text-sm text-slate-500 mt-1">Alter your selected RSVP category or clear the search filters</p>
        </div>
      ) : (
        <div className="bg-white border border-slate-200 rounded-xl shadow-sm overflow-hidden">
          {/* Mobile card view (hidden on sm+) */}
          <div className="sm:hidden divide-y divide-slate-100">
            {filtered.map(a => {
              const ticketRef = a.travelMode === 'flight' || a.travelMode === 'both' ? (a.flightPnr || a.flightNo || '-') : (a.cabBookingRef || '-');
              return (
                <div key={a.id} className="p-4 space-y-3">
                  <div className="flex items-start justify-between gap-2">
                    <div>
                      <div className="font-semibold text-slate-900">{a.name}</div>
                      <div className="text-xs text-slate-500">{a.email || '-'}</div>
                      <div className="text-xs text-slate-500 mt-0.5">{a.phone || '-'}</div>
                      <div className="flex flex-wrap gap-2 mt-1">
                        {a.dietary && a.dietary !== 'None' && <span className="bg-green-50 text-green-700 border border-green-200 px-1.5 py-0.5 rounded text-[10px] font-medium">{a.dietary}</span>}
                        {a.bloodGroup && a.bloodGroup !== 'Unknown' && <span className="bg-red-50 text-red-700 border border-red-200 px-1.5 py-0.5 rounded text-[10px] font-bold">{a.bloodGroup}</span>}
                        {a.originCity && <span className="bg-slate-50 text-slate-600 border border-slate-200 px-1.5 py-0.5 rounded text-[10px]">📍 {a.originCity}</span>}
                      </div>
                    </div>
                    <span className={`sh-badge ${rsvpClass[a.rsvp] || 'sh-badge-outline'} shrink-0`}>{a.rsvp || 'pending'}</span>
                  </div>
                  <div className="flex flex-wrap gap-2">
                    {a.role && <span className="bg-slate-100 px-2 py-1 rounded text-xs font-medium text-slate-700">{a.role}</span>}
                    <span className="bg-slate-100 px-2 py-1 rounded text-xs font-semibold text-slate-700 uppercase">{a.travelMode || 'cab'}</span>
                    <span className={`sh-badge ${travelClass[a.travelStatus || 'pending'] || 'sh-badge-outline'}`}>{a.travelStatus || 'pending'}</span>
                    {ticketRef !== '-' && <span className="font-mono text-[10px] text-slate-600 bg-slate-50 border border-slate-200 px-2 py-1 rounded">{ticketRef}</span>}
                  </div>
                  <div className="flex items-center justify-between pt-1 border-t border-slate-100">
                    <div className="text-xs text-slate-600">
                      <span className="font-semibold">Travel:</span> ₹{(parseFloat(a.travelCost) || 0).toLocaleString('en-IN')}
                      {expenseMap[a.id] > 0 && <span className="ml-2"><span className="font-semibold">Claims:</span> ₹{(expenseMap[a.id] || 0).toLocaleString('en-IN')}</span>}
                    </div>
                    <div className="flex items-center gap-1.5">
                      <button className="p-1.5 text-slate-500 hover:text-slate-900 hover:bg-slate-100 rounded-md transition" title="Upload invoice" onClick={() => setInvoiceFor(a)}><Receipt size={14} /></button>
                      <button className="p-1.5 text-slate-500 hover:text-slate-900 hover:bg-slate-100 rounded-md transition" title="Edit" onClick={() => setModal(a)}><Edit2 size={14} /></button>
                      <button className="p-1.5 text-red-500 hover:text-red-700 hover:bg-red-50 rounded-md transition" title="Delete" onClick={() => del(a.id)}><Trash2 size={14} /></button>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
          {/* Desktop table view (hidden on mobile) */}
          <div className="hidden sm:block table-wrap">
            <table className="w-full border-collapse">
              <thead>
                <tr className="bg-slate-50/50 border-b border-slate-200">
                  <th className="py-3 px-4 text-xs font-bold text-slate-500 uppercase tracking-wider">Attendee Profile</th>
                  <th className="py-3 px-4 text-xs font-bold text-slate-500 uppercase tracking-wider">Phone No</th>
                  <th className="py-3 px-4 text-xs font-bold text-slate-500 uppercase tracking-wider">RSVP status</th>
                  <th className="py-3 px-4 text-xs font-bold text-slate-500 uppercase tracking-wider">Department</th>
                  <th className="py-3 px-4 text-xs font-bold text-slate-500 uppercase tracking-wider">Origin City</th>
                  <th className="py-3 px-4 text-xs font-bold text-slate-500 uppercase tracking-wider">Travel Code Info</th>
                  <th className="py-3 px-4 text-xs font-bold text-slate-500 uppercase tracking-wider">Flight/Cab Cost</th>
                  <th className="py-3 px-4 text-xs font-bold text-slate-500 uppercase tracking-wider">Claim Invoices</th>
                  <th className="py-3 px-4 text-xs font-bold text-slate-500 uppercase tracking-wider"></th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {filtered.map(a => {
                  const ticketRef = a.travelMode === 'flight' || a.travelMode === 'both' ? (a.flightPnr || a.flightNo || '-') : (a.cabBookingRef || '-');
                  return (
                    <tr key={a.id} className="hover:bg-slate-50/30 transition">
                      <td className="py-3 px-4">
                        <div className="font-semibold text-slate-900">{a.name}</div>
                        <div className="text-xs text-slate-500">{a.email || '-'}</div>
                        <div className="flex flex-wrap gap-1 mt-1">
                          {a.dietary && a.dietary !== 'None' && <span className="bg-green-50 text-green-700 border border-green-200 px-1.5 py-0.5 rounded text-[10px] font-medium">{a.dietary}</span>}
                          {a.bloodGroup && a.bloodGroup !== 'Unknown' && <span className="bg-red-50 text-red-700 border border-red-200 px-1.5 py-0.5 rounded text-[10px] font-bold">{a.bloodGroup}</span>}
                          {a.originCity && <span className="text-[10px] text-slate-500">📍 {a.originCity}</span>}
                        </div>
                      </td>
                      <td className="py-3 px-4 text-slate-600 text-sm">{a.phone || '-'}</td>
                      <td className="py-3 px-4">
                        <span className={`sh-badge ${rsvpClass[a.rsvp] || 'sh-badge-outline'}`}>{a.rsvp}</span>
                      </td>
                      <td className="py-3 px-4 text-slate-600 text-sm font-medium">{a.role}</td>
                      <td className="py-3 px-4 text-slate-600 text-sm font-medium">{a.originCity || '-'}</td>
                      <td className="py-3 px-4">
                        <div className="flex flex-col gap-1">
                          <div className="flex items-center gap-1.5">
                            <span className="text-xs font-semibold text-slate-700 uppercase">{a.travelMode || 'cab'}</span>
                            {ticketRef !== '-' && <span className="text-[10px] font-mono text-slate-500">({ticketRef})</span>}
                          </div>
                          <span className={`sh-badge ${travelClass[a.travelStatus || 'pending'] || 'sh-badge-outline'} text-[10px] py-0.5 px-2 w-max`}>
                            {a.travelStatus || 'pending'}
                          </span>
                        </div>
                      </td>
                      <td className="py-3 px-4 text-sm font-mono font-semibold text-slate-900">
                        ₹{(parseFloat(a.travelCost) || 0).toLocaleString('en-IN')}
                      </td>
                      <td className="py-3 px-4 text-sm font-mono font-semibold text-slate-900">
                        ₹{(expenseMap[a.id] || 0).toLocaleString('en-IN')}
                      </td>
                      <td className="py-3 px-4 text-right">
                        <div className="flex items-center gap-1.5 justify-end">
                          <button className="p-1.5 text-slate-500 hover:text-slate-900 hover:bg-slate-100 rounded-md transition" title="Upload invoice" onClick={() => setInvoiceFor(a)}><Receipt size={14} /></button>
                          <button className="p-1.5 text-slate-500 hover:text-slate-900 hover:bg-slate-100 rounded-md transition" title="Edit Attendee" onClick={() => setModal(a)}><Edit2 size={14} /></button>
                          <button className="p-1.5 text-red-500 hover:text-red-700 hover:bg-red-50 rounded-md transition" title="Delete Attendee" onClick={() => del(a.id)}><Trash2 size={14} /></button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {modal && <Modal person={modal === 'new' ? null : modal} onSave={save} onClose={() => setModal(null)} />}
      {invoiceFor && <InvoiceModal attendee={invoiceFor} onSave={saveInvoice} onClose={() => setInvoiceFor(null)} />}
    </div>
  );
}
